import clsx from 'clsx';
import { ComponentProps, PropsWithChildren, ReactElement } from 'react';
import {
  DisclosureProps as AriaDisclosureProps,
  Button,
  Disclosure,
  DisclosurePanel,
  Heading,
} from 'react-aria-components';
import Icon from '~/components/data-display/Icon';
import { VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import IconChevronDown from '~/icons/icon-chevron-down.svg?react';
import styles from './DisclosureItem.module.scss';

type TriggerMode = 'full' | 'icon';
interface DisclosureProps extends Omit<AriaDisclosureProps, 'children'> {
  title: string | ReactElement;
  titleProps?: ComponentProps<typeof Text>;
  noBorder?: boolean;
  triggerMode?: TriggerMode;
  headingStyle?: string;
  panelStyle?: string;
  titleExtraContent?: React.ReactNode;
}

const DisclosureItem = ({
  title,
  titleProps,
  children,
  noBorder,
  triggerMode = 'full',
  headingStyle,
  panelStyle,
  titleExtraContent,
  ...props
}: PropsWithChildren<DisclosureProps>) => {
  const headingContent = (
    <>
      {typeof title === 'string' ? (
        <Text variant="subtitle1" {...titleProps}>
          {title}
        </Text>
      ) : (
        title
      )}
    </>
  );

  return (
    <Disclosure {...props}>
      <Heading>
        <VStack gap={1}>
          {triggerMode === 'full' ? (
            <Button slot="trigger" className={clsx(styles.disclosureHeading, styles.trigger, headingStyle)}>
              {headingContent}
              <Icon component={IconChevronDown} size="md" />
            </Button>
          ) : (
            <div className={clsx(styles.disclosureHeading, headingStyle)}>
              {headingContent}{' '}
              <Button slot="trigger" className={clsx(styles.trigger, styles.iconTrigger)}>
                <Icon component={IconChevronDown} size="md" />{' '}
              </Button>
            </div>
          )}

          {titleExtraContent}
        </VStack>
      </Heading>
      <DisclosurePanel
        className={clsx(styles.disclosurePanel, panelStyle, {
          [styles.noBorder!]: noBorder,
        })}
      >
        {children}
      </DisclosurePanel>
    </Disclosure>
  );
};

export default DisclosureItem;
